/* eslint-disable object-property-newline */
import React, {
    createContext, useState, useEffect, useContext,
    useCallback, useMemo, useRef
} from 'react';
import { compareObjects, isEmptyObject } from 'lib';

const DefaultHeaderHeight = 30;
const DefaultRowHeight = 40;

export const TableContext = createContext({});

export const TableProvider = ({ children }) => {
    const tableRef = useRef();
    const mainBodyRef = useRef();

    const [columns, setColumns] = useState([]);
    const [data, setData] = useState([]);
    const [rowKey, setRowKey] = useState('');
    const [currentRow, setCurrentRow] = useState('');
    const [headerTooltip, setHeaderTooltip] = useState(true);
    const [filterMode, setFilterMode] = useState('and');
    const [pageLength, setPageLength] = useState(0);
    const [resizable, setResizable] = useState(true);
    const [langConfig, setLangConfig] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(DefaultHeaderHeight);
    const [columnBordered, setColumnBordered] = useState(true);
    const [hover, setHover] = useState(true);
    const [selectable, setSelectable] = useState(true);
    const [cellEllipsis, setCellEllipsis] = useState(true);
    const [actionGradient, setActionGradient] = useState(true);
    const [propCheckedRows, setPropCheckedRows] = useState([]);
    const [checkboxTitleDisabled, setCheckboxTitleDisabled] = useState(false);
    const [checkboxDisabled, setCheckboxDisabled] = useState([]);
    const [propExpandedRows, setPropExpandedRows] = useState([]);
    const [expandRowShadow, setExpandRowShadow] = useState(true);
    const [editable, setEditable] = useState(false);
    const [handleFilterColumn, setHandleFilterColumn] = useState(null);

    const ScrollbarWidth = useMemo(() => {
        const scrollDiv = document.createElement('div');
        scrollDiv.className = 'table-scrollbar-measure';
        document.body.appendChild(scrollDiv);
        const result = scrollDiv.offsetWidth - scrollDiv.clientWidth;
        document.body.removeChild(scrollDiv);
        return result;
    }, []);

    const [editedData, setEditedData] = useState([]);
    const [sortInfo, setSortInfo] = useState({});
    const [filterInfo, setFilterInfo] = useState({});
    const filterData = useMemo(() => {
        let result = editedData?.map((item) => ({ ...item })) || [];
        const filterIndex = [];
        Object.entries(filterInfo).forEach(([key, value]) => {
            const thisCol = columns?.find((col) => col.dataIndex === key) || {};
            if (thisCol && value.length) {
                const { dataIndex, filterRender } = thisCol;
                const currentResult = [];
                result.forEach((row, index) => {
                    let text = row[dataIndex];
                    if (typeof text === 'object' && text) {
                        text = JSON.stringify(text);
                    }
                    text = filterRender ? filterRender(row[dataIndex], row, index) : text;
                    let isIncluded = value.includes(text);
                    if (Array.isArray(text)) {
                        isIncluded = text.some((x) => value.includes(x));
                    }
                    if (isIncluded) {
                        currentResult.push(row);
                        if (filterMode === 'or' && !filterIndex.includes(index)) {
                            filterIndex.push(index);
                        }
                    }
                });
                if (filterMode === 'and') {
                    result = currentResult;
                }
            }
        });
        if (filterIndex.length) {
            result = result.filter((_, index) => filterIndex.includes(index));
        }
        if (sortInfo.dataIndex) {
            const { dataIndex, orderBy } = sortInfo;
            result.sort(compareObjects(dataIndex, orderBy));
        }
        return result;
    }, [columns, editedData, filterMode, filterInfo, sortInfo]);

    const [page, setPage] = useState(1);
    useEffect(() => {
        setPage(1);
    }, [filterData]);
    const pageData = useMemo(() => {
        let result = filterData.map((item) => ({ ...item }));
        if (pageLength) {
            const startIndex = pageLength * (page - 1);
            result = result.slice(startIndex, startIndex + pageLength);
        }
        return result;
    }, [filterData, pageLength, page]);

    const scrollLeftRef = useRef(0);
    const scrollTopRef = useRef(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const [hoverRow, setHoverRow] = useState('');
    const [selectedRow, setSelectedRow] = useState('');
    const onSelectRowRef = useRef();

    const [cellEllipsisRowHeight, setCellEllipsisRowHeight] = useState({});
    const timeout = useRef();
    useEffect(() => {
        const key = Object.keys(cellEllipsisRowHeight)[0];
        if (key && key !== String(selectedRow)) {
            timeout.current = setTimeout(() => {
                setCellEllipsisRowHeight({});
            }, 100);
        }
    }, [selectedRow, cellEllipsisRowHeight]);
    useEffect(() => {
        setCellEllipsisRowHeight({});
    }, [columns, data]);

    const [tableCheckedRows, setTableCheckedRows] = useState([]);
    const onChangeCheckedRowsRef = useRef();
    const checkedRows = propCheckedRows || tableCheckedRows;
    useEffect(() => {
        if (checkedRows.some((key) => !filterData.some((row) => row[rowKey] === key))) {
            const result = checkedRows.filter((key) => (
                filterData.some((row) => row[rowKey] === key)
            ));
            if (!propCheckedRows) {
                setTableCheckedRows(result);
            }
            onChangeCheckedRowsRef.current?.(result);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterData, rowKey]);

    const [tableExpandedRows, setTableExpandedRows] = useState([]);
    const onChangeExpandedRowsRef = useRef();
    const expandedRows = propExpandedRows || tableExpandedRows;
    useEffect(() => {
        if (expandedRows.some((key) => !filterData.some((row) => row[rowKey] === key))) {
            const result = expandedRows.filter((key) => (
                filterData.some((row) => row[rowKey] === key)
            ));
            if (!propExpandedRows) {
                setTableExpandedRows(result);
            }
            onChangeExpandedRowsRef.current?.(result);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterData, rowKey]);

    const [editingCell, setEditingCell] = useState({});
    const [batchEditValues, setBatchEditValues] = useState({});
    const clickEditingCell = useRef(false);
    const editedValue = useRef({});
    const editableColumns = useMemo(() => {
        const { left, main, right } = (columns || [])
            .filter((col) => col.isShow !== false && col.type)
            .reduce((prev, col) => {
                if (col.fixed === 'left') {
                    prev.left.push(col);
                } else if (col.fixed === 'right') {
                    prev.right.push(col);
                } else {
                    prev.main.push(col);
                }
                return prev;
            }, { left: [], right: [], main: [] });
        return [...left, ...main, ...right];
    }, [columns]);
    const { prevCol, nextCol, prevRow, nextRow } = useMemo(() => {
        if (!isEmptyObject(editingCell)) {
            const currColIndex = editableColumns.findIndex((col) => (
                col.dataIndex === editingCell.col
            ));
            const currRowIndex = pageData?.findIndex((row) => row[rowKey] === editingCell.row);
            return {
                prevCol: editableColumns[currColIndex - 1]?.dataIndex,
                nextCol: editableColumns[currColIndex + 1]?.dataIndex,
                prevRow: pageData[currRowIndex - 1]?.[rowKey],
                nextRow: pageData[currRowIndex + 1]?.[rowKey]
            };
        }
        return {};
    }, [editingCell, editableColumns, pageData, rowKey]);
    const updateEditedData = useCallback(() => {
        if (editedValue.current.formatValue !== undefined) {
            const findCol = editableColumns.find((x) => x.dataIndex === editingCell.col);
            const findRow = editedData.find((x) => x[rowKey] === editingCell.row);
            if (findCol && findRow) {
                const newEditedData = editedData.map((item) => ({ ...item }));
                const editedRow = newEditedData.find((x) => x[rowKey] === editingCell.row);
                editedRow[findCol.dataIndex] = editedValue.current.formatValue;
                setEditedData(newEditedData);
            }
        }
    }, [editableColumns, editedData, rowKey, editingCell]);
    const onChangeEditingCell = useCallback(({ col, row }) => {
        clickEditingCell.current = true;
        setTimeout(() => { clickEditingCell.current = false; });
        if (!isEmptyObject(editingCell)) {
            updateEditedData();
        }
        if (editable && editableColumns.some((x) => x.dataIndex === col)) {
            setEditingCell((prev) => (
                prev.col !== col || prev.row !== row ? ({ col, row }) : prev
            ));
        } else {
            setEditingCell({});
        }
        editedValue.current = {};
    }, [editable, editableColumns, editingCell, updateEditedData]);
    useEffect(() => {
        if (!editable ||
            !isEmptyObject(editingCell) && (
                !editableColumns.some((col) => col.dataIndex === editingCell.col) ||
                !pageData.some((row) => row[rowKey] === editingCell.row)
            )
        ) {
            setEditingCell({});
            editedValue.current = {};
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editable, editableColumns, pageData]);
    useEffect(() => {
        const onClick = () => {
            if (!isEmptyObject(editingCell) && !clickEditingCell.current) {
                onChangeEditingCell({});
            }
        };
        const onKeydown = (e) => {
            if (editable && !isEmptyObject(editingCell) && !e.isComposing) {
                const findCol = editableColumns.find((x) => x.dataIndex === editingCell.col);
                const handleSetEditingCell = (callback, scroll) => {
                    e.preventDefault();
                    updateEditedData();
                    setEditingCell(callback);
                    editedValue.current = {};
                    let columnIndex;
                    let rowIndex;
                    if (scroll === 'prevCol') {
                        columnIndex = editableColumns.findIndex((x) => x.dataIndex === prevCol);
                    } else if (scroll === 'nextCol') {
                        columnIndex = editableColumns.findIndex((x) => x.dataIndex === nextCol);
                    } else if (scroll === 'prevRow') {
                        rowIndex = pageData.findIndex((x) => x[rowKey] === prevRow);
                    } else if (scroll === 'nextRow') {
                        rowIndex = pageData.findIndex((x) => x[rowKey] === nextRow);
                    }
                    mainBodyRef.current.scrollToItem({ columnIndex, rowIndex });
                };
                if (e.key === 'Enter' && nextRow) {
                    handleSetEditingCell((prev) => ({ ...prev, row: nextRow }), 'nextRow');
                    setSelectedRow(nextRow[rowKey]);
                    onSelectRowRef.current?.(nextRow[rowKey]);
                } else if (e.key === 'Tab' && nextCol) {
                    handleSetEditingCell((prev) => ({ ...prev, col: nextCol }), 'nextCol');
                } else if (e.key === 'ArrowUp' && prevRow !== undefined) {
                    if (['Date', 'DateTime', 'Time'].includes(findCol?.type)) {
                        e.preventDefault();
                    }
                    if (findCol?.type !== 'Select') {
                        handleSetEditingCell((prev) => ({ ...prev, row: prevRow }), 'prevRow');
                        setSelectedRow(prevRow[rowKey]);
                        onSelectRowRef.current?.(prevRow[rowKey]);
                    }
                } else if (e.key === 'ArrowDown' && nextRow) {
                    if (['Date', 'DateTime', 'Time'].includes(findCol?.type)) {
                        e.preventDefault();
                    }
                    if (findCol?.type !== 'Select') {
                        handleSetEditingCell((prev) => ({ ...prev, row: nextRow }), 'nextRow');
                        setSelectedRow(nextRow[rowKey]);
                        onSelectRowRef.current?.(nextRow[rowKey]);
                    }
                } else if (e.key === 'ArrowLeft') {
                    if (['Date', 'DateTime', 'Time'].includes(findCol?.type)) {
                        e.preventDefault();
                        if (e.target.selectionStart !== 0) {
                            let range;
                            if (e.target.selectionStart === 3) {
                                range = [0, 2];
                            } else if (e.target.selectionStart === 5) {
                                range = [0, 4];
                            } else if (e.target.selectionStart === 8) {
                                range = [5, 7];
                            } else if (e.target.selectionStart === 11) {
                                range = [8, 10];
                            } else if (e.target.selectionStart === 14) {
                                range = [11, 13];
                            }
                            if (range) {
                                e.target.setSelectionRange(...range);
                            }
                            return;
                        }
                    }
                    if (prevCol) {
                        handleSetEditingCell((prev) => ({ ...prev, col: prevCol }), 'prevCol');
                    }
                } else if (e.key === 'ArrowRight') {
                    if (['Date', 'DateTime', 'Time'].includes(findCol?.type)) {
                        e.preventDefault();
                        if (e.target.selectionEnd !== e.target.value.length) {
                            const start = e.target.value.search(/[a-zA-Z]/);
                            let range;
                            if (e.target.selectionStart === 0) {
                                range = findCol?.type !== 'Time' ? [5, 7] : [3, 5];
                            } else if (e.target.selectionStart === 5) {
                                range = [8, 10];
                            } else if (e.target.selectionStart === 8) {
                                range = [11, 13];
                            } else if (e.target.selectionStart === 11) {
                                range = [14, 16];
                            }
                            if (range && (range[0] <= start || start === -1)) {
                                e.target.setSelectionRange(...range);
                                return;
                            }
                        }
                    }
                    if (nextCol) {
                        handleSetEditingCell((prev) => ({ ...prev, col: nextCol }), 'nextCol');
                    }
                } else if (e.key === 'Delete') {
                    setEditedData((prev) => {
                        const result = prev.map((item) => ({ ...item }));
                        const findRow = result.find((item) => item[rowKey] === editingCell.row);
                        if (findCol && findRow) {
                            findRow[findCol.dataIndex] = '';
                            editedValue.current = { value: undefined, formatValue: '' };
                            findCol.onChange?.(findRow, undefined);
                        }
                        return result;
                    });
                    const currEditingCell = editingCell;
                    setEditingCell({});
                    setTimeout(() => setEditingCell(currEditingCell));
                }
            }
        };
        const onMouseup = () => {
            if (!isEmptyObject(batchEditValues)) {
                setEditedData((prev) => {
                    const result = prev.map((item) => ({ ...item }));
                    const { col, rowStart, rowEnd, value, formatValue } = batchEditValues;
                    const findCol = editableColumns.find((x) => x.dataIndex === col);
                    const min = Math.min(rowStart, rowEnd);
                    const max = Math.max(rowStart, rowEnd);
                    for (let i = min; i <= max; i += 1) {
                        result[i][col] = formatValue;
                        findCol.onChange?.(result[i], value);
                    }
                    return result;
                });
                setBatchEditValues({});
                const currEditingCell = editingCell;
                setEditingCell({});
                setTimeout(() => setEditingCell(currEditingCell));
            }
        };
        window.addEventListener('click', onClick);
        window.addEventListener('keydown', onKeydown);
        window.addEventListener('mouseup', onMouseup);
        return () => {
            window.removeEventListener('click', onClick);
            window.removeEventListener('keydown', onKeydown);
            window.removeEventListener('mouseup', onMouseup);
        };
    }, [editable, editingCell, editableColumns, pageData, rowKey,
        prevCol, nextCol, prevRow, nextRow, onChangeEditingCell, updateEditedData,
        batchEditValues]);

    const [draggingRow, setDraggingRow] = useState('');
    const draggingRowIndex = useMemo(() => (
        pageData.findIndex((row) => draggingRow && row[rowKey] === draggingRow)
    ), [pageData, rowKey, draggingRow]);
    const draggingOffset = useRef(0);
    const destinationRow = useRef('');
    const onDragEndRef = useRef();
    const onChangeDraggingRow = useCallback((key) => {
        setDraggingRow(key);
        const propertyValue = key ? 'none' : 'auto';
        document.body.style.pointerEvents = propertyValue;
        document.body.style.userSelect = propertyValue;
        const modal = document.getElementsByClassName('ant-modal-content');
        for (let i = 0; i < modal.length; i += 1) {
            modal[i].style.pointerEvents = propertyValue;
        }
    }, []);
    useEffect(() => {
        const onMouseMove = (e) => {
            const { top: tableTop } = tableRef.current.getBoundingClientRect();
            let draggingTop = e.clientY + scrollTopRef.current -
                (tableTop + headerHeight + draggingOffset.current);
            const { top: draggingRowTop, height: draggingRowHeight } = mainBodyRef.current
                .getPosition({ rowIndex: draggingRowIndex });
            const { top: lastRowTop, height: lastRowHeight } = mainBodyRef.current
                .getPosition({ rowIndex: pageData.length - 1 });
            const maxTop = lastRowTop + lastRowHeight - draggingRowHeight;
            if (draggingTop < 0) {
                draggingTop = 0;
            } else if (draggingTop > maxTop) {
                draggingTop = maxTop;
            }
            const draggingBottom = draggingTop + draggingRowHeight;
            const draggingEl = tableRef.current.querySelectorAll(`.table-row-${draggingRow}`);
            for (let i = 0; i < draggingEl.length; i += 1) {
                draggingEl[i].style.transform = `translateY(${draggingTop - draggingRowTop}px)`;
            }
            destinationRow.current = '';
            for (let rowIndex = draggingRowIndex - 1; rowIndex >= 0; rowIndex -= 1) {
                const { top: currTop, height: currHeight } = mainBodyRef.current
                    .getPosition({ rowIndex });
                const currCenter = currTop + currHeight * 1 / 2;
                const currKey = pageData[rowIndex][rowKey];
                const currEl = tableRef.current.querySelectorAll(`.table-row-${currKey}`);
                for (let i = 0; i < currEl.length; i += 1) {
                    currEl[i].style.transform = draggingTop < currCenter ?
                        `translateY(${draggingRowHeight}px)` : '';
                }
                if (draggingTop < currCenter) {
                    destinationRow.current = currKey;
                }
            }
            for (let rowIndex = draggingRowIndex + 1; rowIndex < pageData.length; rowIndex += 1) {
                const { top: currTop, height: currHeight } = mainBodyRef.current
                    .getPosition({ rowIndex });
                const currCenter = currTop + currHeight * 1 / 2;
                const currKey = pageData[rowIndex][rowKey];
                const currEl = tableRef.current.querySelectorAll(`.table-row-${currKey}`);
                for (let i = 0; i < currEl.length; i += 1) {
                    currEl[i].style.transform = draggingBottom > currCenter ?
                        `translateY(-${draggingRowHeight}px)` : '';
                }
                if (draggingBottom > currCenter) {
                    destinationRow.current = currKey;
                }
            }
        };
        const onMouseUp = () => {
            const allRowEl = tableRef.current.querySelectorAll('.table-row');
            for (let i = 0; i < allRowEl.length; i += 1) {
                allRowEl[i].style.transform = '';
            }
            onChangeDraggingRow('');
            if (destinationRow.current) {
                const draggedData = editedData.map((item) => ({ ...item }));
                const source = draggedData.findIndex((row) => row[rowKey] === draggingRow);
                const destination = draggedData.findIndex((row) => (
                    row[rowKey] === destinationRow.current
                ));
                const [draggingItem] = draggedData.splice(source, 1);
                draggedData.splice(destination, 0, draggingItem);
                if (onDragEndRef.current) {
                    onDragEndRef.current?.({ key: draggingRow, source, destination });
                } else {
                    setEditedData(draggedData);
                }
                destinationRow.current = '';
            }
        };
        if (draggingRow) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [draggingRow, draggingRowIndex, headerHeight, pageData, rowKey, editedData, onChangeDraggingRow]);

    return (
        <TableContext.Provider
            value={{
                /* const variable */
                DefaultHeaderHeight,
                DefaultRowHeight,
                ScrollbarWidth,
                /* props */
                columns, setColumns,
                data, setData,
                rowKey, setRowKey,
                currentRow, setCurrentRow,
                headerTooltip, setHeaderTooltip,
                filterMode, setFilterMode,
                pageLength, setPageLength,
                resizable, setResizable,
                langConfig, setLangConfig,
                headerHeight, setHeaderHeight,
                columnBordered, setColumnBordered,
                hover, setHover,
                selectable, setSelectable,
                cellEllipsis, setCellEllipsis,
                actionGradient, setActionGradient,
                propCheckedRows, setPropCheckedRows,
                checkboxTitleDisabled, setCheckboxTitleDisabled,
                checkboxDisabled, setCheckboxDisabled,
                propExpandedRows, setPropExpandedRows,
                expandRowShadow, setExpandRowShadow,
                editable, setEditable,
                handleFilterColumn, setHandleFilterColumn,
                /* component state */
                sortInfo, setSortInfo,
                filterInfo, setFilterInfo,
                page, setPage,
                filterData, pageData,
                scrollLeft, setScrollLeft,
                scrollTop, setScrollTop,
                hoverRow, setHoverRow,
                selectedRow, setSelectedRow,
                cellEllipsisRowHeight, setCellEllipsisRowHeight,
                tableCheckedRows, setTableCheckedRows,
                checkedRows,
                tableExpandedRows, setTableExpandedRows,
                expandedRows,
                editedData, setEditedData,
                editingCell, setEditingCell: onChangeEditingCell,
                batchEditValues, setBatchEditValues,
                draggingRow, setDraggingRow: onChangeDraggingRow,
                draggingRowIndex,
                /* ref */
                tableRef,
                mainBodyRef,
                scrollLeftRef,
                scrollTopRef,
                timeout,
                onSelectRowRef,
                onChangeCheckedRowsRef,
                onChangeExpandedRowsRef,
                editedValue,
                draggingOffset,
                onDragEndRef
            }}
        >
            {children}
        </TableContext.Provider>
    );
};

const useSetState = (setState, state, checkUpdate) => {
    useEffect(() => {
        setState?.((prev) => {
            if (checkUpdate?.(prev, state) === false) {
                return prev;
            }
            return state;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);
};

export const useSetContext = ({
    columns,
    data,
    rowKey,
    currentRow,
    headerTooltip,
    filterMode,
    pageLength,
    resizable,
    langConfig,
    headerHeight,
    columnBordered,
    hover,
    selectable,
    cellEllipsis,
    actionGradient,
    checkedRows,
    checkboxTitleDisabled,
    checkboxDisabled,
    expandedRows,
    expandRowShadow,
    editable,
    handleFilterColumn,
    defaultSort
}) => {
    const {
        setColumns,
        setData,
        setRowKey,
        setCurrentRow,
        setHeaderTooltip,
        setFilterMode,
        setPageLength,
        setResizable,
        setLangConfig,
        setHeaderHeight,
        setColumnBordered,
        setHover,
        setSelectable,
        setCellEllipsis,
        setActionGradient,
        setPropCheckedRows,
        setCheckboxTitleDisabled,
        setCheckboxDisabled,
        setPropExpandedRows,
        setExpandRowShadow,
        setEditable,
        setEditedData,
        setSortInfo,
        setHandleFilterColumn
    } = useContext(TableContext);

    useSetState(setColumns, columns);
    useSetState(setData, data);
    useSetState(setRowKey, rowKey);
    useSetState(setCurrentRow, currentRow);
    useSetState(setHeaderTooltip, headerTooltip);
    useSetState(setFilterMode, filterMode);
    useSetState(setPageLength, pageLength);
    useSetState(setResizable, resizable);
    useSetState(setLangConfig, langConfig);
    useSetState(setHeaderHeight, headerHeight);
    useSetState(setColumnBordered, columnBordered);
    useSetState(setHover, hover);
    useSetState(setSelectable, selectable);
    useSetState(setCellEllipsis, cellEllipsis);
    useSetState(setActionGradient, actionGradient);
    useSetState(setPropCheckedRows, checkedRows);
    useSetState(setCheckboxTitleDisabled, checkboxTitleDisabled);
    useSetState(setCheckboxDisabled, checkboxDisabled, (prev, curr) => (
        JSON.stringify(prev) !== JSON.stringify(curr)
    ));
    useSetState(setPropExpandedRows, expandedRows);
    useSetState(setExpandRowShadow, expandRowShadow);
    useSetState(setEditable, editable);
    useSetState(setEditedData, data);
    useSetState(setHandleFilterColumn, handleFilterColumn);
    useEffect(() => {
        if (defaultSort) {
            setSortInfo(defaultSort);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export const useSetRef = ({
    onSelectRow,
    onChangeCheckedRows,
    onChangeExpandedRows,
    onDragEnd
}) => {
    const {
        onSelectRowRef,
        onChangeCheckedRowsRef,
        onChangeExpandedRowsRef,
        onDragEndRef
    } = useContext(TableContext);

    useEffect(() => {
        onSelectRowRef.current = onSelectRow;
    }, [onSelectRowRef, onSelectRow]);
    useEffect(() => {
        onChangeCheckedRowsRef.current = onChangeCheckedRows;
    }, [onChangeCheckedRowsRef, onChangeCheckedRows]);
    useEffect(() => {
        onChangeExpandedRowsRef.current = onChangeExpandedRows;
    }, [onChangeExpandedRowsRef, onChangeExpandedRows]);
    useEffect(() => {
        onDragEndRef.current = onDragEnd;
    }, [onDragEndRef, onDragEnd]);
};
